import React, { useState, useEffect, useRef } from "react";
import {  toast } from "react-toastify";
import {
  Button,
  Box,
  Typography,
  useMediaQuery,
  Paper,
  IconButton,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ebenchLogo from "../../../images/ebench_logo.png";
import "react-toastify/dist/ReactToastify.css";
import { useResendOtp } from "../../../Hooks/useResendOtp";
import { verifyCandidateOtp } from "../../../Services/authService";
import setAuthorizationToken from "../../../utils/setAutorization";

const OtpVerify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const LoginData = location.state || JSON.parse(sessionStorage.getItem("otpData"));
  const { mutate: resendOtp } = useResendOtp();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [shake, setShake] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResendVisible, setIsResendVisible] = useState(false);

  const inputRefs = useRef([]);

  const email = LoginData?.email;
  const otp = otpValues.join("");
  const data = { otp, email };

  useEffect(() => {
    return () => sessionStorage.removeItem("otpData");
  }, []);
  /* Auto focus */
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  /* Handle OTP input change */
  const handleInputChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return;

    const updatedOtp = [...otpValues];
    updatedOtp[index] = value;
    setOtpValues(updatedOtp);

    // Move forward only if typing a number
    if (value !== "" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  /**Handle backspace navigation */
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (otpValues[index] === "" && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else {
        const updatedOtp = [...otpValues];
        updatedOtp[index] = "";
        setOtpValues(updatedOtp);
      }
    }
  };

  /** Timer */
  useEffect(() => {
    if (secondsLeft === 0) return setIsResendVisible(true);

    const timer = setTimeout(() => setSecondsLeft((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  /* Submit OTP */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!otp) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return toast.error("Please enter your OTP!");
    }

    try {
      await verifyCandidateOtp(data);
      toast.success("Verification Successful!");
      if (LoginData?.logIn) {
        localStorage.setItem("adminid", LoginData?.adminId);
        setAuthorizationToken(LoginData?.token);

        setTimeout(() => {
          navigate(
            LoginData?.userType === "VENDOR"
              ? "/vendor_dashboard"
              : LoginData?.userType === "ADMIN"
              ? "/admin"
              : "/candidate_dashboard"
          );
        }, 800);
      } else navigate("/login");
    } catch {
      setShake(true);
      console.error("Invalid OTP, please try again.");
    }
  };

  /* Resend OTP */
  const handleResendOtp = () => {
    setIsLoading(true);
    resendOtp(
      { email, userType: LoginData?.userType },
      {
        onSuccess: () => {
          setIsLoading(false);
          setSecondsLeft(60);
          setIsResendVisible(false);
        },
        onError: () => {
          setIsLoading(false);
          toast.error("Failed to resend OTP. Try again.");
        },
      }
    );
  };

  return (
    <>
      {/* HEADER */}
      <header
        className="shadow-sm bg-white fixed-top"
        style={{
          padding: "10px 12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Link to="/">
          <img
            src={ebenchLogo}
            alt="logo"
            style={{
              width: isMobile ? "120px" : "150px",
              height: "auto",
            }}
          />
        </Link>

        <Link to="/login">
          <button
            className="btn btn-outline-primary-custom"
            style={{
              padding: isMobile ? "6px 12px" : "6px 20px",
              fontWeight: 600,
              fontSize: isMobile ? "13px" : "15px",
            }}
          >
            Login
          </button>
        </Link>
      </header>

      {/* MAIN OTP SECTION */}
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#eef5ff",
          pt: 10,
          px: 2,
        }}
      >
        <Paper
          elevation={4}
          sx={{
            width: "100%",
            maxWidth: "480px",
            p: { xs: 3, md: 4 },
            textAlign: "center",
            borderRadius: "20px",
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(8px)",
          }}
        >
          <Typography variant="h5" fontWeight={700} sx={{ color: "#123b56" }}>
            Verify OTP
          </Typography>

          <Typography sx={{ fontSize: "14px", color: "#666", mt: 1, mb: 3 }}>
            Enter the 6-digit code sent to your registered email / phone.
          </Typography>

          {/* OTP INPUTS */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              gap: isMobile ? 1.3 : 2,
              animation: shake ? "shake .4s" : "",
              mx: "auto",
              maxWidth: "350px",
            }}
          >
            {otpValues.map((val, idx) => (
              <input
                key={idx}
                maxLength={1}
                value={val}
                ref={(el) => (inputRefs.current[idx] = el)}
                onChange={(e) => handleInputChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                style={{
                  width: "100%",
                  height: isMobile ? "50px" : "60px",
                  textAlign: "center",
                  fontSize: "20px",
                  fontWeight: "600",
                  borderRadius: "10px",
                  border: "2px solid #b9c6d5",
                  outline: "none",
                  transition: "0.3s",
                }}
              />
            ))}
          </Box>

          {/* SUBMIT */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            sx={{
              mt: 4,
              py: 1.5,
              fontWeight: 600,
              borderRadius: "12px",
              background: "linear-gradient(135deg,#286a94,#1e4e70)",
            }}
          >
            Verify OTP
          </Button>

          {/* RESEND */}
          <Box mt={4}>
            {isResendVisible ? (
              <>
                <Typography sx={{ mb: 1, fontSize: "14px" }}>
                  Didn’t receive the code?
                </Typography>
                <Button
                  variant="outlined"
                  disabled={isLoading}
                  onClick={handleResendOtp}
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    px: 3,
                    color: "#286a94",
                    borderColor: "#286a94",
                  }}
                >
                  {isLoading ? "Sending..." : "Resend OTP"}
                </Button>
              </>
            ) : (
              <Typography sx={{ color: "#286a94", fontWeight: 600 }}>
                Resend available in {secondsLeft}s
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>

      {/* ANIMATIONS */}
      <style>
        {`
          @keyframes shake {
            0%,100% { transform: translateX(0); }
            25% { transform: translateX(-6px); }
            50% { transform: translateX(6px); }
          }
        `}
      </style>
    </>
  );
};

export default OtpVerify;
